import React, { useState, useEffect, useRef } from 'react';
import api from '../../api/api';
import Heading from "../../components/ui/Heading";
import SearchInput from "../../components/ui/SearchInput";
const ReportTool = ({ userName }) => {
    const [reportTitles, setReportTitles] = useState([]);
    const [partyList, setPartyList] = useState([]);
    const [reportTitle, setReportTitle] = useState('');
    const [fromDate, setFromDate] = useState('2025-08-01');
    const [toDate, setToDate] = useState(() => {
  const today = new Date();
  return today.toISOString().split("T")[0]; 
});
    const [selectedParty, setSelectedParty] = useState('All');
    const [reportData, setReportData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [reportHeaders, setReportHeaders] = useState([]);
    const [showReportPage, setShowReportPage] = useState(false);
    const [reportSearchQuery, setReportSearchQuery] = useState('');
    const [partySearchQuery, setPartySearchQuery] = useState('');
    const [isPartyDropdownOpen, setIsPartyDropdownOpen] = useState(false);
    const [reportSearchTerm, setReportSearchTerm] = useState('');
    const [isReportDropdownOpen, setIsReportDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const reportDropdownRef = useRef(null);

    useEffect(() => {
        const fetchReportAndPartyList = async () => {
            try {
                const res = await api.GetDataReport({ User: userName });
                if (res?.Table) {
                    const uniqueReports = [
                        ...new Map(res.Table.map(item => [item.ReportTypeName, item])).values()
                    ].map(r => r.ReportTypeName);
                    setReportTitles(uniqueReports);
                    // ❌ Removed auto-set default report
                }
                if (res?.Table1) {
                    const uniqueParties = [
                        ...new Map(res.Table1.map(item => [item.Vid, item])).values()
                    ].map(p => ({
                        vid: p.Vid,
                        vname: p.Vname,
                        address: p.Vcode || ''
                    }));
                    setPartyList(uniqueParties);
                }
            } catch (error) {
                console.error('Error fetching report/party list:', error);
            }
        };

        fetchReportAndPartyList();
    }, [userName]);

    const filteredPartyList = partyList.filter(party =>
        party.vname.toLowerCase().includes(partySearchQuery.toLowerCase()) ||
        String(party.vid).includes(partySearchQuery) ||
        party.address.toLowerCase().includes(partySearchQuery.toLowerCase())
    );

    const filteredReportTitles = reportTitles.filter(title =>
        title.toLowerCase().includes(reportSearchTerm.toLowerCase())
    );

    const filteredReportData = reportData.filter(row => {
        if (!reportSearchQuery) return true;
        const searchString = reportSearchQuery.toLowerCase();
        return Object.values(row).some(value =>
            String(value).toLowerCase().includes(searchString)
        );
    });

    const fetchReport = async () => {
        setIsLoading(true);
        try {
            const foundParty = partyList.find(p => p.vname === selectedParty);
            const vidParam = selectedParty === 'All' ? '' : (foundParty?.vid ?? '');

            const res = await api.SflowersReports({
                Vid: vidParam,
                ReportType: reportTitle,
                FromDate: fromDate,
                Todate: toDate,
                UserName: userName
            });
            const data = res?.Table || [];
            if (data.length > 0) {
                setReportHeaders(Object.keys(data[0]));
            } else {
                setReportHeaders([]);
            }
            setReportData(data);
            setShowReportPage(true);
        } catch (error) {
            console.error('Error fetching report data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // === Export Functions ===
    const handleExportExcel = () => {
        const reportCriteria = [
            ['Report Title:', reportTitle],
            ['From Date:', fromDate],
            ['To Date:', toDate],
            ['Selected Party:', selectedParty],
            []
        ];
        const headers = reportHeaders.map(h => h.charAt(0).toUpperCase() + h.slice(1));
        const dataRows = filteredReportData.map(row => reportHeaders.map(h => row[h]));
        const allData = reportCriteria.concat([headers]).concat(dataRows);

        let csvContent = "";
        allData.forEach(rowArray => {
            let row = rowArray.join(",");
            csvContent += row + "\n";
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${reportTitle}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    const handleExportPDF = () => {
        const printWindow = window.open('', '', 'height=600,width=800');
        printWindow.document.write('<html><head><title>Report</title>');
        printWindow.document.write('<style>table {width: 100%; border-collapse: collapse;} th, td {border: 1px solid #ddd; padding: 8px; text-align: left;} h1, h3 {margin: 0;}</style>');
        printWindow.document.write('</head><body>');
        printWindow.document.write(`<h1>${reportTitle}</h1>`);
        printWindow.document.write(`<h3>From Date: ${fromDate} &nbsp;&nbsp; To Date: ${toDate}</h3>`);
        printWindow.document.write(`<h3>Selected Party: ${selectedParty}</h3>`);

        if (filteredReportData.length > 0) {
            printWindow.document.write('<table><thead><tr>');
            reportHeaders.forEach(header => {
                printWindow.document.write(`<th>${header}</th>`);
            });
            printWindow.document.write('</tr></thead><tbody>');
            filteredReportData.forEach(row => {
                printWindow.document.write('<tr>');
                reportHeaders.forEach(header => {
                    printWindow.document.write(`<td>${row[header]}</td>`);
                });
                printWindow.document.write('</tr>');
            });
            printWindow.document.write('</tbody></table>');
        } else {
            printWindow.document.write('<p>No data to display.</p>');
        }

        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.print();
    };

    const handleSendMail = () => {
        const subject = `Report: ${reportTitle}`;
        const body = `Please find the ${reportTitle} report for the period ${fromDate} to ${toDate}.`;
        window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    const handleSendWhatsApp = () => {
        const message = `Here is the ${reportTitle} report from ${fromDate} to ${toDate}.`;
        window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
    };

    const handlePartySelect = (partyName) => {
        setSelectedParty(partyName);
        setIsPartyDropdownOpen(false);
    };

    const handleReportSelect = (title) => {
        setReportTitle(title);
        setReportSearchTerm(title);
        setIsReportDropdownOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsPartyDropdownOpen(false);
            }
            if (reportDropdownRef.current && !reportDropdownRef.current.contains(event.target)) {
                setIsReportDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const renderFormPage = () => (
        <>
            <Heading>Reporting Module</Heading>

            {/* Responsive grid */}
            <div className="grid grid-cols-1 gap-4 p-4 bg-white rounded-lg shadow sm:grid-cols-2 lg:grid-cols-4">
                {/* Report search dropdown */}
                <div ref={reportDropdownRef} className="relative">
                    <label className="block mb-1 text-sm font-medium text-gray-900">Report</label>
                    <input
                        type="text"
                        placeholder="Search report..."
                        value={reportSearchTerm}
                        onChange={(e) => {
                            setReportSearchTerm(e.target.value);
                            setReportTitle('');
                            setIsReportDropdownOpen(true);
                        }}
                        onFocus={() => setIsReportDropdownOpen(true)}
                        className="w-full p-2 text-sm border border-gray-300 rounded-md sm:text-base"
                    />
                    {isReportDropdownOpen && (
                        <ul className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border border-gray-300 rounded-md shadow max-h-52">
                            {filteredReportTitles.map((title) => (
                                <li
                                    key={title}
                                    onClick={() => handleReportSelect(title)}
                                    className={`p-2 cursor-pointer hover:bg-gray-100 ${reportTitle === title ? 'bg-gray-200' : ''}`}
                                >
                                    {title}
                                </li>
                            ))}
                            {filteredReportTitles.length === 0 && (
                                <li className="p-2 text-gray-500">No results found</li>
                            )}
                        </ul>
                    )}
                </div>

                {/* Party search dropdown */}
                <div ref={dropdownRef} className="relative">
                    <label className="block mb-1 text-sm font-medium text-gray-900">Party</label>
                    <input
                        type="text"
                        placeholder="Search or select..."
                        value={selectedParty === 'All' ? partySearchQuery : selectedParty}
                        onChange={(e) => {
                            setPartySearchQuery(e.target.value);
                            setSelectedParty(null);
                            setIsPartyDropdownOpen(true);
                        }}
                        onFocus={() => setIsPartyDropdownOpen(true)}
                        className="w-full p-2 text-sm border border-gray-300 rounded-md sm:text-base"
                    />
                    {isPartyDropdownOpen && (
                        <ul className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border border-gray-300 rounded-md shadow max-h-52">
                            {filteredPartyList.map((party) => (
                                <li
                                    key={party.vid}
                                    onClick={() => handlePartySelect(party.vname)}
                                    className={`p-2 cursor-pointer hover:bg-gray-100 ${selectedParty === party.vname ? 'bg-gray-200' : ''}`}
                                >
                                    <span className="font-semibold text-blue-600">{party.vid}</span> {party.vname}
                                    <div className="text-xs text-gray-500">{party.address}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Dates */}
                <div>
                    <label className="block mb-1 text-sm font-medium text-gray-900">From Date</label>
                    <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className="w-full p-2 text-sm border border-gray-300 rounded-md sm:text-base"
                    />
                </div>
                <div>
                    <label className="block mb-1 text-sm font-medium text-gray-900">To Date</label>
                    <input
                        type="date"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className="w-full p-2 text-sm border border-gray-300 rounded-md sm:text-base"
                    />
                </div>
            </div>

            {/* Button */}
            <div className="flex justify-center mt-4">
                <button
                    onClick={fetchReport}
                    disabled={isLoading}
                    className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md shadow sm:text-base hover:bg-blue-700 disabled:bg-gray-400"
                >
                    {isLoading ? 'Generating...' : 'Generate Report'}
                </button>
            </div>
        </>
    );

    // const renderReportPage = () => (
    //     <>
    //         {/* Top controls */}
    //         <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
    //             <button
    //                 onClick={() => setShowReportPage(false)}
    //                 className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300 sm:text-base"
    //             >
    //                 ← Back
    //             </button>
    //             <h1 className="text-lg font-semibold text-center sm:text-xl sm:text-left">{reportTitle}</h1>
    //             <input
    //                 type="text"
    //                 placeholder="Search..."
    //                 value={reportSearchQuery}
    //                 onChange={(e) => setReportSearchQuery(e.target.value)}
    //                 className="w-full p-2 text-sm border border-gray-300 rounded-md sm:w-auto sm:text-base"
    //             />
    //         </div>

    //         <div className="flex flex-wrap gap-2 mb-4">
    //             <button onClick={handleExportExcel}
    //              className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-green-500 rounded-full shadow-sm transition-all duration-200 hover:bg-green-600 hover:shadow-md sm:flex-none"  >
    //                 Excel 📊
    //             </button>
    //             <button onClick={handleExportPDF}
    //                className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-red-500 rounded-full shadow-sm transition-all duration-200 hover:bg-red-600 hover:shadow-md sm:flex-none"  >
    //                 PDF 📄
    //             </button>
    //         </div>


    //         {/* Table scrollable on mobile */}
    //         <div className="overflow-x-auto border rounded shadow">
    //             {filteredReportData.length > 0 ? (
    //                 <table className="min-w-full text-sm border-collapse sm:text-base">
    //                     <thead>
    //                         <tr>
    //                             {reportHeaders.map(header => (
    //                                 <th key={header} className="p-2 border">{header}</th>
    //                             ))}
    //                         </tr>
    //                     </thead>
    //                     <tbody>
    //                         {filteredReportData.map((row, i) => (
    //                             <tr key={i}>
    //                                 {reportHeaders.map(header => (
    //                                     <td key={header} className="p-2 border">{row[header]}</td>
    //                                 ))}
    //                             </tr>
    //                         ))}
    //                     </tbody>
    //                 </table>
    //             ) : (
    //                 <div className="p-4 text-center">No data available</div>
    //             )}
    //         </div>
    //     </>
    // );

    const renderReportPage = () => (
        <>
            {/* Top controls */}
            <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
                <button
                    onClick={() => setShowReportPage(false)}
                    className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300 sm:text-base"
                    
                >
                    ← Back
                </button>
                <h1 className="text-lg font-semibold text-center sm:text-xl sm:text-left">
                    {reportTitle}
                </h1>
                 <p><span className="font-semibold"></span> {selectedParty}/ {fromDate}/ {toDate}</p>
                <input
                    type="text"
                    placeholder="Search..."
                    value={reportSearchQuery}
                    onChange={(e) => setReportSearchQuery(e.target.value)}
                    className="w-full p-2 text-sm border border-gray-300 rounded-md sm:w-auto sm:text-base"
                />
            </div>

           

            <div className="flex flex-wrap gap-2 mb-4">
                <button
                    onClick={handleExportExcel}
                    className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-green-500 rounded-full shadow-sm transition-all duration-200 hover:bg-green-600 hover:shadow-md sm:flex-none"
                >
                    Excel 📊
                </button>
                <button
                    onClick={handleExportPDF}
                    className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-red-500 rounded-full shadow-sm transition-all duration-200 hover:bg-red-600 hover:shadow-md sm:flex-none"
                >
                    PDF 📄
                </button>
            </div>

            {/* Table scrollable on mobile */}
            <div className="overflow-x-auto border rounded shadow">
                {filteredReportData.length > 0 ? (
                    <table className="min-w-full text-sm border-collapse sm:text-base">
                        <thead>
                            <tr>
                                {reportHeaders.map(header => (
                                    <th key={header} className="p-2 border">{header}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredReportData.map((row, i) => (
                                <tr key={i}>
                                    {reportHeaders.map(header => (
                                        <td key={header} className="p-2 border">{row[header]}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="p-4 text-center">No data available</div>
                )}
            </div>
        </>
    );


    return (
        <div className="p-4">
            {showReportPage ? renderReportPage() : renderFormPage()}
        </div>
    );
};

export default ReportTool;
